import { DayPicker } from 'react-day-picker';
import { AnimatePresence, motion } from 'framer-motion';
import 'react-day-picker/dist/style.css';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../utils/api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

function formatDateMMDDYYYY(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

export default function AttendanceTab() {
  const { user, isCoach, isAdmin, canMarkTeamAttendance } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [absences, setAbsences] = useState({}); // { athleteName: [{ date, created_by }, ...] }
  const [selectedAthlete, setSelectedAthlete] = useState('');
  const [selectedDates, setSelectedDates] = useState([]);
  const [expandedAthletes, setExpandedAthletes] = useState({});
  
  // Calendar view state (coaches/admins only)
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'calendar'
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [calendarAbsences, setCalendarAbsences] = useState({});
  const [expandedDay, setExpandedDay] = useState(null);
  
  // Attendance rankings state
  const [attendanceRankings, setAttendanceRankings] = useState([]);
  const [attendanceUserRank, setAttendanceUserRank] = useState(null);
  const [attendanceUserStats, setAttendanceUserStats] = useState(null);
  const [attendanceTotalParticipants, setAttendanceTotalParticipants] = useState(0);
  const [attendanceLookbackDate, setAttendanceLookbackDate] = useState(null);
  const [attendanceTotalSessions, setAttendanceTotalSessions] = useState(0);
  const [attendanceAvailableTeams, setAttendanceAvailableTeams] = useState([]);
  const [attendanceSelectedTeams, setAttendanceSelectedTeams] = useState([]);

  useEffect(() => {
    fetchAthletes();
    fetchAttendance();
    fetchAttendanceRankings();
  }, []);

  useEffect(() => {
    fetchAttendanceRankings();
  }, [attendanceSelectedTeams]);

  useEffect(() => {
    if (canMarkTeamAttendance() && viewMode === 'calendar') {
      fetchCalendarAbsences();
    }
  }, [viewMode, calendarMonth]);

  const fetchAttendanceRankings = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/attendance/rankings`;
      if (attendanceSelectedTeams.length > 0) {
        url += `?teams=${encodeURIComponent(attendanceSelectedTeams.join(','))}`;
      }
      
      const response = await fetch(url, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setAttendanceRankings(data.rankings || []);
        setAttendanceUserRank(data.userRank);
        setAttendanceUserStats(data.userStats);
        setAttendanceTotalParticipants(data.totalParticipants || 0);
        setAttendanceLookbackDate(data.lookbackDate);
        setAttendanceTotalSessions(data.totalPossibleSessions || 0);
        if (data.availableTeams) {
          setAttendanceAvailableTeams(data.availableTeams);
        }
      } else {
        console.error('Failed to fetch attendance rankings');
      }
    } catch (error) {
      console.error('Error fetching attendance rankings:', error);
    }
  };

  const toggleAttendanceTeamFilter = (team) => {
    setAttendanceSelectedTeams(prev => 
      prev.includes(team) 
        ? prev.filter(t => t !== team)
        : [...prev, team]
    );
  };

  const fetchCalendarAbsences = async () => {
    try {
      const token = localStorage.getItem('token');
      const year = calendarMonth.getFullYear();
      const month = calendarMonth.getMonth() + 1;
      
      const response = await fetch(`${API_URL}/api/attendance/calendar?year=${year}&month=${month}`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setCalendarAbsences(data.absencesByDate || {});
      } else {
        console.error('Failed to fetch calendar absences');
      }
    } catch (error) {
      console.error('Error fetching calendar absences:', error);
    }
  };

  const fetchAthletes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/athletes`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setAthletes(data);
      } else {
        console.error('Failed to fetch athletes');
      }
    } catch (error) {
      console.error('Error fetching athletes:', error);
    }
  };

  const fetchAttendance = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/attendance/future`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        const absenceMap = {};
        data.forEach((record) => {
          if (!absenceMap[record.athlete_name]) {
            absenceMap[record.athlete_name] = [];
          }
          // Store both date and created_by info
          absenceMap[record.athlete_name].push({
            date: record.absence_date,
            created_by: record.created_by
          });
        });
        setAbsences(absenceMap);
      } else {
        console.error('Failed to fetch attendance');
      }
    } catch (error) {
      console.error('Error fetching attendance:', error);
    }
  };

  const markAbsent = async () => {
    if (selectedAthlete && selectedDates.length > 0) {
      const athlete = athletes.find((a) => a.name === selectedAthlete);
      if (!athlete) {
        alert('Athlete not found');
        return;
      }

      const formattedDates = selectedDates.map((date) =>
        formatDateMMDDYYYY(new Date(date))
      );

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/attendance`, {
          method: 'POST',
        headers: getAuthHeaders(token),
          body: JSON.stringify({
            athleteId: athlete.id,
            dates: formattedDates,
          }),
        });

        if (response.ok) {
          await fetchAttendance();
          alert(`${selectedAthlete} marked absent: ${formattedDates.join(', ')}`);
          setSelectedAthlete('');
          setSelectedDates([]);
        } else {
          const error = await response.json();
          alert(`Failed to mark absent: ${error.error || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error marking absent:', error);
        alert('Error marking absent. Please try again.');
      }
    } else {
      alert('Please select an athlete and at least one date.');
    }
  };

  const removeAbsence = async (athleteName, date) => {
    if (confirm(`Remove ${athleteName}'s absence for ${date}?`)) {
      const athlete = athletes.find((a) => a.name === athleteName);
      if (!athlete) {
        alert('Athlete not found');
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${API_URL}/api/attendance/athlete/${athlete.id}/date/${encodeURIComponent(date)}`,
          {
            method: 'DELETE',
            headers: getAuthHeaders(token)
          }
        );

        if (response.ok) {
          await fetchAttendance();
        } else {
          const data = await response.json();
          alert(data.error || 'Failed to remove absence.');
        }
      } catch (error) {
        console.error('Error removing absence:', error);
        alert('Error removing absence. Please try again.');
      }
    }
  };

  // Check if user can remove a specific absence
  const canRemoveAbsence = (createdBy) => {
    // Coaches and admins can remove any absence
    if (isCoach() || isAdmin()) return true;
    // Otherwise, only the creator can remove
    return createdBy === user?.id;
  };

  const today = formatDateMMDDYYYY(new Date());

  const filteredAbsences = Object.entries(absences).reduce(
    (acc, [athlete, dates]) => {
      const futureDates = dates.filter((date) => date >= today);
      if (futureDates.length > 0) acc[athlete] = futureDates;
      return acc;
    },
    {}
  );

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(calendarMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setCalendarMonth(newDate);
    setExpandedDay(null);
  };

  const renderCalendarView = () => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(calendarMonth);
    const days = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 sm:h-28 bg-gray-50"></div>);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${String(month + 1).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
      const dayAbsences = calendarAbsences[dateStr] || [];
      const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
      const isPast = new Date(year, month, day) < new Date().setHours(0, 0, 0, 0);
      const isExpanded = expandedDay === dateStr;
      const maxVisible = 3;
      const hasMore = dayAbsences.length > maxVisible;

      days.push(
        <div
          key={day}
          className={`h-24 sm:h-28 border border-gray-200 p-1 overflow-hidden relative ${
            isToday ? 'bg-blue-50 border-blue-300' : isPast ? 'bg-gray-100' : 'bg-white'
          }`}
        >
          <div className={`text-xs sm:text-sm font-semibold mb-1 ${isToday ? 'text-blue-600' : isPast ? 'text-gray-400' : 'text-gray-700'}`}>
            {day}
          </div>
          
          {dayAbsences.length > 0 && (
            <div className="space-y-0.5">
              {dayAbsences.slice(0, isExpanded ? undefined : maxVisible).map((absence, idx) => (
                <div
                  key={idx}
                  className="text-xs bg-red-100 text-red-700 px-1 py-0.5 rounded truncate"
                  title={`${absence.athleteName}${absence.team ? ` (${absence.team})` : ''}`}
                >
                  {absence.athleteName}
                </div>
              ))}
              
              {hasMore && !isExpanded && (
                <button
                  onClick={() => setExpandedDay(dateStr)}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  +{dayAbsences.length - maxVisible} more
                </button>
              )}
            </div>
          )}

          {/* Expanded overlay */}
          {isExpanded && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setExpandedDay(null)}>
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                  <div>
                    <h3 className="font-bold text-lg">
                      {new Date(year, month, day).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </h3>
                    <p className="text-sm text-gray-600">{dayAbsences.length} athlete{dayAbsences.length !== 1 ? 's' : ''} absent</p>
                  </div>
                  <button
                    onClick={() => setExpandedDay(null)}
                    className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                  >
                    ×
                  </button>
                </div>
                <div className="p-4 overflow-y-auto max-h-[60vh]">
                  <div className="space-y-2">
                    {dayAbsences.map((absence, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-800">{absence.athleteName}</p>
                          {absence.team && (
                            <p className="text-xs text-gray-500">{absence.team}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Calendar header */}
        <div className="flex items-center justify-between p-4 bg-gray-50 border-b">
          <button
            onClick={() => navigateMonth(-1)}
            className="p-2 hover:bg-gray-200 rounded-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 className="text-lg font-bold text-gray-800">
            {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
          <button
            onClick={() => navigateMonth(1)}
            className="p-2 hover:bg-gray-200 rounded-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Day names header */}
        <div className="grid grid-cols-7 bg-gray-100 border-b">
          {dayNames.map(day => (
            <div key={day} className="p-2 text-center text-xs sm:text-sm font-semibold text-gray-600">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7">
          {days}
        </div>

        {/* Legend */}
        <div className="p-3 bg-gray-50 border-t flex items-center gap-4 text-xs text-gray-600">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-red-100 rounded"></div>
            <span>Absent</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-blue-50 border border-blue-300 rounded"></div>
            <span>Today</span>
          </div>
        </div>
      </div>
    );
  };

  // Render attendance rankings sidebar
  const renderAttendanceRankings = () => (
    <div className="lg:w-72 lg:flex-shrink-0">
      <div className="lg:sticky lg:top-4">
        <div className="bg-gradient-to-b from-green-50 to-emerald-50 border border-green-200 rounded-lg overflow-hidden">
          {/* Header */}
          <div className="p-3 border-b border-green-200 bg-gradient-to-r from-green-100 to-emerald-100">
            <div className="flex items-center gap-2">
              <span className="text-xl">✅</span>
              <span className="font-bold text-green-800">Attendance</span>
            </div>
            {attendanceUserRank && (
              <p className="text-sm text-green-700 mt-1">
                You're <span className="font-bold">#{attendanceUserRank}</span> of {attendanceTotalParticipants}
              </p>
            )}
          </div>

          {/* Team Filter - Checkboxes */}
          {attendanceAvailableTeams.length > 0 && (
            <div className="px-3 py-2 border-b border-green-200 bg-white/50">
              <p className="text-xs font-medium text-green-700 mb-1.5">Filter by Team:</p>
              <div className="space-y-1 max-h-24 overflow-y-auto">
                {attendanceAvailableTeams.map((team) => (
                  <label key={team} className="flex items-center gap-2 cursor-pointer hover:bg-green-100/50 px-1 py-0.5 rounded">
                    <input
                      type="checkbox"
                      checked={attendanceSelectedTeams.includes(team)}
                      onChange={() => toggleAttendanceTeamFilter(team)}
                      className="w-3.5 h-3.5 text-green-600 rounded border-green-300 focus:ring-green-400"
                    />
                    <span className="text-sm text-gray-700">{team}</span>
                  </label>
                ))}
              </div>
              {attendanceSelectedTeams.length > 0 && (
                <button
                  onClick={() => setAttendanceSelectedTeams([])}
                  className="text-xs text-green-600 hover:text-green-800 mt-1"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}

          {/* Lookback Date */}
          {attendanceLookbackDate ? (
            <div className="px-3 py-1.5 bg-white/50 border-b border-green-200 text-xs text-green-700">
              Since {new Date(attendanceLookbackDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              <span className="text-gray-500 ml-1">({attendanceTotalSessions} sessions)</span>
            </div>
          ) : (
            <div className="px-3 py-2 bg-yellow-50 border-b border-yellow-200 text-xs text-yellow-700">
              ⚠️ Set a Training & Attendance Lookback Date in Club Management
            </div>
          )}

          {/* Rankings List */}
          <div className="divide-y divide-green-100 bg-white/80 max-h-[calc(100vh-300px)] lg:max-h-[calc(100vh-220px)] overflow-y-auto">
            {attendanceRankings.length === 0 ? (
              <div className="px-3 py-4 text-center text-green-700 text-sm">
                {attendanceLookbackDate ? 'No athletes found' : 'Set lookback date to see rankings'}
              </div>
            ) : (
              attendanceRankings.map((r) => (
                <div 
                  key={r.athleteId} 
                  className={`flex items-center gap-2 px-3 py-2 ${
                    r.isCurrentUser ? 'bg-green-100' : ''
                  }`}
                >
                  {/* Rank */}
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full font-bold text-xs flex-shrink-0 ${
                    r.rank === 1 ? 'bg-amber-400 text-amber-900' :
                    r.rank === 2 ? 'bg-gray-300 text-gray-700' :
                    r.rank === 3 ? 'bg-orange-300 text-orange-800' :
                    'bg-green-200/50 text-green-700'
                  }`}>
                    {r.rank === 1 ? '🥇' : r.rank === 2 ? '🥈' : r.rank === 3 ? '🥉' : r.rank}
                  </div>
                  
                  {/* Name */}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${r.isCurrentUser ? 'text-green-900' : 'text-gray-800'}`}>
                      {r.athleteName}
                      {r.isCurrentUser && <span className="text-xs ml-1 text-green-600">(You)</span>}
                    </p>
                    {r.team && attendanceSelectedTeams.length === 0 && (
                      <p className="text-xs text-gray-500 truncate">{r.team}</p>
                    )}
                  </div>
                  
                  {/* Attendance % */}
                  <div className="text-right flex-shrink-0">
                    <p className={`text-sm font-bold ${
                      r.attendancePercent >= 90 ? 'text-green-600' :
                      r.attendancePercent >= 75 ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {r.attendancePercent}%
                    </p>
                    <p className="text-xs text-gray-500">{r.attendedSessions}/{r.totalSessions}</p>
                  </div>
                </div>
              ))
            )}
          </div>
          
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 p-2 sm:p-6">
      {/* Left Sidebar - Attendance Rankings */}
      {renderAttendanceRankings()}

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h2 className="text-xl sm:text-2xl font-bold">Attendance</h2>
          
          {/* View toggle for coaches/admins */}
          {canMarkTeamAttendance() && (
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white shadow text-theme-primary'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                List View
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'calendar'
                    ? 'bg-white shadow text-theme-primary'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Calendar View
              </button>
            </div>
          )}
        </div>

        {/* Calendar View */}
        {viewMode === 'calendar' && canMarkTeamAttendance() && (
          <div className="mb-6">
            {renderCalendarView()}
          </div>
        )}

      {/* List View (original content) */}
      {viewMode === 'list' && (
        <>
      <div className="mb-4 sm:mb-6">
        {/* Info message for non-coaches */}
        {!canMarkTeamAttendance() && (
          <p className="text-sm text-gray-600 mb-3 bg-blue-50 p-3 rounded-lg">
            💡 You can only log absences for yourself. Contact a coach or admin to log absences for others.
          </p>
        )}
        
        {/* Staff see the club; coxswains see their team; others only themselves */}
        {(() => {
          const canSeeTeamAthletes = canMarkTeamAttendance();
          const availableAthletes = canSeeTeamAthletes
            ? (user?.role === 'coxswain' && user?.team
              ? athletes.filter((a) => a.team === user.team || (Array.isArray(a.teams) && a.teams.includes(user.team)))
              : athletes)
            : athletes.filter(a => user?.athleteId && parseInt(a.id) === parseInt(user.athleteId));
          const canSeeAllAthletes = canSeeTeamAthletes;
          
          return (
            <select
              value={selectedAthlete}
              onChange={(e) => setSelectedAthlete(e.target.value)}
              className="border p-3 sm:p-2 rounded w-full mb-4 text-blue-900 bg-white text-base"
            >
              <option value="">{canSeeAllAthletes ? 'Select Athlete' : 'Select Yourself'}</option>
              {availableAthletes.map((athlete) => (
                <option key={athlete.id} value={athlete.name}>
                  {athlete.name}
                </option>
              ))}
            </select>
          );
        })()}

        <div className="flex flex-col mb-4">
          <label className="font-semibold mb-2 text-sm sm:text-base">Mark Absent Dates:</label>
          {/* Mobile: Native date input */}
          <input
            type="date"
            onChange={(e) => {
              if (e.target.value) {
                const date = new Date(e.target.value + 'T00:00:00');
                setSelectedDates(prev => {
                  // Toggle date selection
                  const exists = prev.find(d => formatDateMMDDYYYY(d) === formatDateMMDDYYYY(date));
                  if (exists) {
                    return prev.filter(d => formatDateMMDDYYYY(d) !== formatDateMMDDYYYY(date));
                  }
                  return [...prev, date];
                });
              }
            }}
            min={new Date().toISOString().split('T')[0]}
            className="border p-3 sm:p-2 rounded bg-white text-blue-900 text-base sm:hidden mb-2"
          />
          {/* Desktop: Calendar picker */}
          <div className="hidden sm:block">
          <DayPicker
            mode="multiple"
            selected={selectedDates}
            onSelect={setSelectedDates}
            disabled={{ before: new Date() }}
            className="rounded-lg border p-4 bg-white"
          />
          </div>

          {selectedDates.length > 0 && (
            <div className="text-xs sm:text-sm text-gray-600 mt-2 p-2 bg-gray-50 rounded">
              <span className="font-medium">Selected ({selectedDates.length}):</span>{' '}
              <div className="flex flex-wrap gap-1 mt-1">
                {selectedDates.map((date, i) => (
                  <span key={i} className="bg-theme-bg-secondary text-theme-primary px-2 py-1 rounded text-xs">
                    {formatDateMMDDYYYY(date)}
                    <button 
                      onClick={() => setSelectedDates(prev => prev.filter((_, idx) => idx !== i))}
                      className="ml-1 text-red-500 hover:text-red-700"
                    >×</button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <button
          onClick={markAbsent}
          className="btn-theme-primary px-6 py-3 rounded-lg w-full sm:w-auto"
        >
          Mark Absent
        </button>
      </div>

      <h3 className="font-semibold text-base sm:text-lg mb-3">Recorded Absences</h3>

      {Object.entries(absences).length === 0 ? (
        <p className="text-gray-500 text-sm sm:text-base">No absences recorded.</p>
      ) : (
        <div className="space-y-2 sm:space-y-4">
          {Object.entries(filteredAbsences).map(([athlete, dates]) => {
          const isExpanded = expandedAthletes[athlete] || false;

          return (
              <div key={athlete} className="border rounded shadow-sm overflow-hidden">
              <button
                onClick={() =>
                  setExpandedAthletes((prev) => ({
                    ...prev,
                    [athlete]: !isExpanded,
                  }))
                }
                  className="w-full text-left px-3 sm:px-4 py-3 sm:py-2 bg-gray-100 hover:bg-gray-200 font-semibold flex justify-between items-center text-sm sm:text-base"
              >
                  <span className="flex items-center gap-2">
                    {athlete}
                    <span className="text-xs bg-gray-300 px-2 py-0.5 rounded-full">{dates.length}</span>
                  </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 transform transition-transform duration-300 ${
                    isExpanded ? 'rotate-90' : 'rotate-0'
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>

              <AnimatePresence initial={false}>
                {isExpanded && (
                  <motion.div
                    key="absences"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                      className="overflow-hidden px-3 sm:px-4 py-2 bg-white text-sm text-gray-700"
                  >
                    <ul className="space-y-1">
                      {dates.map((absence, i) => {
                        // Handle both old format (string) and new format (object)
                        const dateStr = typeof absence === 'string' ? absence : absence.date;
                        const createdBy = typeof absence === 'object' ? absence.created_by : null;
                        
                        return (
                          <li
                            key={i}
                            className="flex justify-between items-center bg-gray-50 px-2 sm:px-3 py-2 sm:py-1 rounded hover:bg-gray-100"
                          >
                            <span className="text-xs sm:text-sm">{formatDateMMDDYYYY(new Date(dateStr))}</span>
                            {canRemoveAbsence(createdBy) && (
                              <button
                                onClick={() => removeAbsence(athlete, dateStr)}
                                className="text-red-500 hover:text-red-700 p-1"
                                title="Remove this absence"
                              >
                                <span className="text-sm">✕</span>
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
          })}
        </div>
      )}
        </>
      )}
      </div>
    </div>
  );
}
